import { useEffect, useState } from "react";
import { AxiosError } from "axios";
import { useSelector } from "react-redux";
import { getIfIsaAccountSelector } from "@state/ifIsa/ifIsa.selectors";
import { isIsaReallocationsEnabledSelector } from "@state/remoteConfiguration/remoteConfiguration.selectors";
import { clientInvestSdk } from "@utils/clientInvestSdk";
import { logException } from "@helpers/logException/logException";

/**
 * Gates the "Move to ISA" entry point on whether the investor can actually
 * start a wrap. The cleared-only `availableToInvest` figure in redux ignores
 * in-flight pending wraps, so it overstates headroom. Instead we ask the
 * eligibility endpoint — the same source of truth the flow itself uses: it
 * 200s only when there's real remaining allowance, and lists the deals that
 * can be wrapped. We show the entry point only when that call succeeds with
 * at least one eligible deal.
 */
interface CanShowIsaWrapEntryPoint {
  canWrap: boolean;
  eligibleDealIds: Set<string>;
}

const EMPTY_DEAL_IDS: Set<string> = new Set();

export const useCanShowIsaWrapEntryPoint = (): CanShowIsaWrapEntryPoint => {
  const isIsaReallocationsEnabled = useSelector(
    isIsaReallocationsEnabledSelector
  );
  const ifIsaAccount = useSelector(getIfIsaAccountSelector);
  const isAccountBlocked =
    ifIsaAccount?.status === "PAUSED" || ifIsaAccount?.status === "CLOSED";

  const canCheckEligibility =
    isIsaReallocationsEnabled && !!ifIsaAccount && !isAccountBlocked;

  const [eligibleDealIds, setEligibleDealIds] =
    useState<Set<string>>(EMPTY_DEAL_IDS);

  useEffect(() => {
    if (!canCheckEligibility) {
      setEligibleDealIds(EMPTY_DEAL_IDS);
      return;
    }

    let cancelled = false;

    const checkEligibility = async () => {
      try {
        const { data } = await clientInvestSdk.getIsaWrapEligibility();
        if (!cancelled) {
          setEligibleDealIds(
            new Set(data.eligibleDeals.map((deal) => deal.dealId))
          );
        }
      } catch (error) {
        if (cancelled) return;
        setEligibleDealIds(EMPTY_DEAL_IDS);
        // 400 (no remaining allowance) and 404 (no IF-ISA account) are the
        // expected "can't wrap" signals, not failures — only log anything else.
        const status = (error as AxiosError)?.response?.status;
        if (status !== 400 && status !== 404) {
          logException({
            exception: error,
            message: "useCanShowIsaWrapEntryPoint: eligibility check failed",
          });
        }
      }
    };

    checkEligibility();

    return () => {
      cancelled = true;
    };
  }, [canCheckEligibility]);

  return {
    canWrap: canCheckEligibility && eligibleDealIds.size > 0,
    eligibleDealIds,
  };
};
